import { toast } from 'sonner'
import { useState, useEffect } from 'react'
// API & Types
import { fError } from '@/utils/format-error'
import axios, { endpoints } from '@/lib/axios'
// Components
import { useBoolean } from '@/hooks/use-boolean'
import CustomPagination from '@/components/pagination/custom-pagination'
import LoadingSection from '@/components/loading-indicator/loading-section'

import OrdersTable from '../orders-table'
import OrdersFilter from '../orders-filter'
import { Order, OrderFilters, OrderStatus, ShippingStatus } from '../types'

// Constants
const PAGE_SIZE = 10

export default function OrdersView() {
  const loading = useBoolean(true)

  // State
  const [orders, setOrders] = useState<Order[]>([])
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    page: 0,
    limit: 0,
  })

  const [filters, setFilters] = useState<OrderFilters>({
    page: 1,
    limit: PAGE_SIZE,
    search: '',
    shippingStatus: ShippingStatus.ALL,
    status: OrderStatus.ALL,
    orderBy: 'createdAt:desc',
    shopId: '',
  })

  // Fetch orders data
  const fetchOrders = async () => {
    try {
      loading.onTrue()

      const { data } = await axios.get(endpoints.orders.list, {
        params: {
          ...filters,
          status:
            filters.status === OrderStatus.ALL ? undefined : filters.status,
          shippingStatus:
            filters.shippingStatus === ShippingStatus.ALL
              ? undefined
              : filters.shippingStatus,
        },
      })

      setOrders(data.items)
      setPageMeta({
        total: data.total,
        page: filters.page,
        limit: filters.limit,
      })
    } catch (error) {
      console.error('Error fetching orders:', error)
      toast.error(fError(error))
      setOrders([])
    } finally {
      loading.onFalse()
    }
  }

  // Initial load และ refetch เมื่อ filters เปลี่ยน
  useEffect(() => {
    fetchOrders()
  }, [filters])

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<OrderFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  // Handle order update
  const handleOrderUpdate = (updatedOrder: Order) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order,
      ),
    )
  }

  return (
    <div className="w-full">
      <LoadingSection loading={false}>
        <div className="space-y-6 mb-6">
          {/* Filter Section */}
          <OrdersFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />

          {/* Table Section */}
          <OrdersTable
            orders={orders}
            loading={loading.value}
            onOrderUpdate={handleOrderUpdate}
          />

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={filters.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>
    </div>
  )
}
